import { useLoansManagementController } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/LoansManagement/useLoansManagementController';
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@dealstack/frontend/shared/components/Alert/Alert';
import { DragAndDrop } from '@dealstack/frontend/shared/components/DragAndDrop/DragAndDrop';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { Modal, ModalContent } from '@dealstack/frontend/shared/components/fundament/Modal/Modal';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { EXCEL_SPREADSHEET_TYPE } from '@dealstack/shared/lib/common/business/excel';
import { flexRender, Table } from '@tanstack/react-table';
import { memo } from 'react';
import { VirtualItem } from 'react-virtual';
import styled from 'styled-components';
import { LoansTableItemType } from './useLoansManagementController.types';

interface Props {
	className?: string;
	mipId: string;
}

const LoansManagement = ({ className, mipId }: Props) => {
	const {
		isLoading,
		rowVirtualizer,
		tableContainerRef,
		rows,
		onRefreshTable,
		tanstackTable,
		showBulkEditModal,
		onDownloadXLSXTemplate,
		onCloseModal,
		onOpenModal,
		bulkCreateUpload,
	} = useLoansManagementController(mipId);

	const totalSize: number = rowVirtualizer.totalSize;
	const virtualRows: VirtualItem[] = rowVirtualizer.virtualItems;
	const table: Table<LoansTableItemType> = tanstackTable;

	const paddingTop: number = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
	const paddingBottom: number =
		virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

	return (
		<div className={className}>
			<BulkUploadSection>
				<ButtonV2
					size="s"
					variant="primary"
					buttonTheme="light"
					onClick={onDownloadXLSXTemplate}
				>
					Download XLSX
				</ButtonV2>
				<ButtonV2 onClick={onRefreshTable} size="s" variant="tertiary" buttonTheme="light">
					Refresh
				</ButtonV2>
				<BulkUploadEditWrapper>
					<ButtonV2 onClick={onOpenModal} size="s" variant="primary" buttonTheme="light">
						Bulk edit
					</ButtonV2>
					{showBulkEditModal ? (
						<Modal onClose={onCloseModal}>
							<ModalContent>
								<Alert icon="information-filled" state="warning" size="m">
									<AlertTitle>WARNING</AlertTitle>
									<AlertDescription>
										You are permitted to:
										<ul style={{ listStyle: 'inside' }}>
											<li>
												modify the type, name, funder, amount, and amount
												remaining columns. (Note: altering any other columns
												could lead to adverse consequences).
											</li>
											<li> remove an entire loan row.</li>
											<li>
												create a new loan (without specifying a loanId).
											</li>
										</ul>
									</AlertDescription>
								</Alert>
								<br />
								<DragAndDrop
									uploadFileRequest={bulkCreateUpload}
									accept={{
										[EXCEL_SPREADSHEET_TYPE]: ['.xlsx'],
									}}
									invalidTypeMessage="Error"
								/>
							</ModalContent>
						</Modal>
					) : null}
				</BulkUploadEditWrapper>
			</BulkUploadSection>
			<TableWrapper>
				{isLoading ? <InlineLoader /> : null}
				<div ref={tableContainerRef} className="table_container">
					<table className="table">
						<thead>
							{table.getHeaderGroups().map((headerGroup) => (
								<tr key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										return (
											<th
												key={header.id}
												colSpan={header.colSpan}
												style={{ width: header.getSize() }}
											>
												{header.isPlaceholder ? null : (
													<div>
														{flexRender(
															header.column.columnDef.header,
															header.getContext()
														)}
													</div>
												)}
											</th>
										);
									})}
								</tr>
							))}
						</thead>
						<tbody>
							{paddingTop > 0 && (
								<tr>
									<td style={{ height: `${paddingTop}px` }} />
								</tr>
							)}
							{virtualRows.map((virtualRow) => {
								const row = rows[virtualRow.index];
								return (
									<tr key={row.id}>
										{row.getVisibleCells().map((cell) => {
											return (
												<td key={cell.id}>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext()
													)}
												</td>
											);
										})}
									</tr>
								);
							})}
							{paddingBottom > 0 && (
								<tr>
									<td style={{ height: `${paddingBottom}px` }} />
								</tr>
							)}
						</tbody>
					</table>
				</div>
				<br />
				<div>{tanstackTable.getRowModel().rows.length} Rows</div>
			</TableWrapper>
		</div>
	);
};

const TableWrapper = styled.div`
	border: 1px solid lightgray;

	table {
		border-collapse: collapse;
		border-spacing: 0;
		font-family: arial, sans-serif;
		table-layout: fixed;
		width: 100%;
	}

	thead {
		background: white !important;
		margin: 0;
		position: sticky;
		top: 0;
		opacity: 1;
		z-index: 1;
	}

	th {
		border-bottom: 1px solid lightgray;
		border-right: 1px solid lightgray;
		padding: 2px 4px;
		text-align: left;
	}

	td {
		padding: 6px;
	}

	.table_container {
		border: 1px solid lightgray;
		height: 40rem;
		overflow: auto;
	}
`;
const BulkUploadSection = styled.section`
	display: flex;
	gap: 1rem;
`;

const BulkUploadEditWrapper = styled.div``;

const Memoized = memo(LoansManagement);
const Styled = styled(Memoized)`
	position: relative;

	${BulkUploadSection} {
		margin-bottom: 1rem;
	}
`;

export { Styled as LoansManagement };
